import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import type {
  EligibleStats,
  OffsetPageMeta,
  QueueStats,
} from "../../domain/contracts/http";
import type { PackageNotificationConfigDTO } from "../../domain/contracts/config";
import type { PackageNotificationsOpsStatus } from "../../domain/contracts/ops-status";
import type { PackageNotificationLogDTO } from "../../domain/contracts/queue";
import type { WhatsAppWorkerHealth } from "../../domain/contracts/queue";
import type {
  EligiblePackageNotificationDTO,
  PackageNotificationPackageType,
  PackageNotificationTriggerType,
} from "../../domain/contracts/triggers";

const baseUrl = `${config.apiUrl}/package-notifications`;

export type PaginatedResponse<TItem, TStats = undefined> = {
  items: TItem[];
  meta: OffsetPageMeta;
  stats?: TStats;
};

export type EligibleNotificationsQuery = {
  groupId?: string;
  packageType?: "package" | "private_lesson" | "all";
  triggerType?: "all" | PackageNotificationTriggerType;
  search?: string;
  limit?: number;
  offset?: number;
};

export type EligibleNotificationsResponse = PaginatedResponse<
  EligiblePackageNotificationDTO,
  EligibleStats
>;

export type EnqueueNotificationsResponse = {
  enqueued: number;
  queueIds: string[];
  skipped: Array<{
    studentId: string;
    packageId: string;
    reason: string;
  }>;
};

export type ProcessQueueResponse = {
  processed: number;
  sent: number;
  failed: number;
  skipped: number;
  paused: boolean;
  pauseReason?: string;
};

export type QueueListItem = {
  id: string;
  studentId: string;
  studentName?: string;
  memberId?: string;
  packageType: string;
  packageId: string;
  triggerType: string;
  status: string;
  phoneNumber: string;
  messageBody: string;
  createdAt: string;
  processedAt: string | null;
  lastError: string | null;
};

export type QueueListResponse = PaginatedResponse<QueueListItem, QueueStats>;

export type LogsListResponse = PaginatedResponse<PackageNotificationLogDTO>;

function appendPagination(
  url: URL,
  query?: { limit?: number; offset?: number },
) {
  if (query?.limit != null) url.searchParams.set("limit", String(query.limit));
  if (query?.offset != null) {
    url.searchParams.set("offset", String(query.offset));
  }
}

export type AutoStatusResponse = {
  enabled: boolean;
  autoEnabled: boolean;
  sentToday: number;
  maxToday: number;
  remainingQuota: number;
  isWarmup: boolean;
  warmupUntil: string | null;
  sendWindowStart: string;
  sendWindowEnd: string;
  timezone: string;
  cronExpression: string;
  cronDescription: string;
};

export const packageNotificationsApi = {
  getConfig: (): Promise<PackageNotificationConfigDTO> =>
    fetcher.get<PackageNotificationConfigDTO>(`${baseUrl}/config`),

  getAutoStatus: (): Promise<AutoStatusResponse> =>
    fetcher.get<AutoStatusResponse>(`${baseUrl}/auto-status`),

  getOpsStatus: (): Promise<PackageNotificationsOpsStatus> =>
    fetcher.get<PackageNotificationsOpsStatus>(`${baseUrl}/ops-status`),

  updateConfig: (
    input: Partial<PackageNotificationConfigDTO>,
  ): Promise<PackageNotificationConfigDTO> =>
    fetcher.patch<PackageNotificationConfigDTO>(`${baseUrl}/config`, input),

  getEligible: (
    query?: EligibleNotificationsQuery,
  ): Promise<EligibleNotificationsResponse> => {
    const url = new URL(`${baseUrl}/eligible`);
    if (query?.groupId) url.searchParams.set("groupId", query.groupId);
    if (query?.packageType) {
      url.searchParams.set("packageType", query.packageType);
    }
    if (query?.triggerType && query.triggerType !== "all") {
      url.searchParams.set("triggerType", query.triggerType);
    }
    if (query?.search?.trim()) {
      url.searchParams.set("search", query.search.trim());
    }
    appendPagination(url, query);
    return fetcher
      .get<EligibleNotificationsResponse & { total?: number }>(url.toString())
      .then((response) => {
        const limit = query?.limit ?? 20;
        const offset = query?.offset ?? 0;
        const items = response.items ?? [];
        const meta = response.meta ?? {
          total: response.total ?? items.length,
          limit,
          offset,
        };
        const stats = response.stats ?? {
          byPackageType: { package: 0, private_lesson: 0 },
          byTriggerType: {
            low_sessions: 0,
            low_hours: 0,
            expiring_soon: 0,
            combined: 0,
          },
        };
        return { items, meta, stats };
      });
  },

  enqueue: (input: {
    source?: "manual" | "auto";
    studentIds?: string[];
    packageIds?: string[];
    items?: Array<{
      studentId: string;
      packageType: PackageNotificationPackageType;
      packageId: string;
      triggerType: string;
    }>;
  }): Promise<EnqueueNotificationsResponse> =>
    fetcher.post<EnqueueNotificationsResponse>(`${baseUrl}/enqueue`, {
      source: "manual",
      ...input,
    }),

  process: (input?: {
    batchSize?: number;
    queueIds?: string[];
  }): Promise<ProcessQueueResponse> =>
    fetcher.post<ProcessQueueResponse>(`${baseUrl}/process`, {
      batchSize: input?.batchSize ?? 1,
      queueIds: input?.queueIds,
    }),

  getQueue: (query?: {
    limit?: number;
    offset?: number;
    status?: string;
  }): Promise<QueueListResponse> => {
    const url = new URL(`${baseUrl}/queue`);
    appendPagination(url, query);
    if (query?.status && query.status !== "all") {
      url.searchParams.set("status", query.status);
    }
    return fetcher.get<QueueListResponse>(url.toString()).then((response) => {
      const limit = query?.limit ?? 20;
      const offset = query?.offset ?? 0;
      const items = response.items ?? [];
      return {
        items,
        meta: response.meta ?? {
          total: items.length,
          limit,
          offset,
        },
        stats: response.stats ?? {
          pending: 0,
          failed: 0,
          total: items.length,
        },
      };
    });
  },

  getLogs: (query?: {
    limit?: number;
    offset?: number;
    status?: string;
  }): Promise<LogsListResponse> => {
    const url = new URL(`${baseUrl}/logs`);
    appendPagination(url, query);
    if (query?.status && query.status !== "all") {
      url.searchParams.set("status", query.status);
    }
    return fetcher.get<LogsListResponse>(url.toString()).then((response) => {
      const limit = query?.limit ?? 20;
      const offset = query?.offset ?? 0;
      const items = response.items ?? [];
      return {
        items,
        meta: response.meta ?? {
          total: items.length,
          limit,
          offset,
        },
      };
    });
  },

  getWorkerStatus: (): Promise<WhatsAppWorkerHealth> =>
    fetcher.get<WhatsAppWorkerHealth>(`${baseUrl}/worker-status`),

  getWorkerQr: (): Promise<{ qr: string | null }> =>
    fetcher.get<{ qr: string | null }>(`${baseUrl}/worker-qr`),

  workerTestSend: (body: {
    phone: string;
    message: string;
  }): Promise<{
    ok: boolean;
    messageId?: string;
    sentAt?: string;
    error?: string;
    retryable?: boolean;
  }> => fetcher.post(`${baseUrl}/worker-test-send`, body),

  workerLogout: (): Promise<{ ok: boolean; error?: string }> =>
    fetcher.post(`${baseUrl}/worker-logout`, {}),
};
